import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material'
import ComponentView, { IndexComponentData } from './ComponentView'
import commonActions from '../../actions/common.actions'
import { useDispatch, useSelector } from 'react-redux'
import { subscriptionTags } from '../../assets/constants'
import { selectOverviewIndexComponentsSortedBy } from '../../selectors/common'
import { Security } from '../../proto/generated/security'
import { OverviewWidgetResponse_IndexComponent } from '../../proto/generated/overview-widget'
import websocketActions from '../../actions/websocket.actions'

export type IndexComponentsProps = {
    indexComponents: OverviewWidgetResponse_IndexComponent[]
    showContribution?: boolean
}

const IndexComponents = ({
    indexComponents,
    showContribution = true,
}: IndexComponentsProps) => {
    const dispatch = useDispatch()
    const initialVisibleLimit = 10
    const [limit, setLimit] = useState(initialVisibleLimit)
    const [isExpanded, setIsExpanded] = useState(false)
    const indexComponentsSortedBy = useSelector(selectOverviewIndexComponentsSortedBy)
    const [securitiesToSubscribe, setSecuritiesToSubscribe] = useState<Security[]>([])
    const resolvedSortCriteria = !showContribution && (indexComponentsSortedBy === 'maxChange' || !indexComponentsSortedBy)
        ? 'descending_change'
        : indexComponentsSortedBy
    const sortIndexComponents = useCallback((indexComponents: IndexComponentData[], criteria: string): IndexComponentData[] => {
        const sortByProperty = function (criteria: string) {
            return function (a: IndexComponentData, b: IndexComponentData) {
                switch (criteria) {
                    case 'ascending_symbol': {
                        return a.security!!.symbol!!.localeCompare(b.security!!.symbol!!)
                    }
                    case 'descending_symbol': {
                        return b.security!!.symbol!!.localeCompare(a.security!!.symbol!!)
                    }
                    case 'descending_change': {
                        return Math.abs(b.changePercentage ?? 0) - Math.abs(a.changePercentage ?? 0)
                    }
                    case 'ascending_change': {
                        return Math.abs(a.changePercentage ?? 0) - Math.abs(b.changePercentage ?? 0)
                    }
                    case 'descending_contribution': {
                        return Math.abs(b.contribution ?? 0) === Math.abs(a.contribution ?? 0)
                            ? Math.abs(b.weightage ?? 0) - Math.abs(a.weightage ?? 0)
                            : Math.abs(b.contribution ?? 0) - Math.abs(a.contribution ?? 0)
                    }
                    case 'ascending_contribution': {
                        return Math.abs(b.contribution ?? 0) === Math.abs(a.contribution ?? 0)
                            ? Math.abs(a.weightage ?? 0) - Math.abs(b.weightage ?? 0)
                            : Math.abs(a.contribution ?? 0) - Math.abs(b.contribution ?? 0)
                    }
                    case 'ascending_ltp': {
                        return (a.ltp ?? 0) - (b.ltp ?? 0)
                    }
                    case 'descending_ltp': {
                        return (b.ltp ?? 0) - (a.ltp ?? 0)
                    }
                    default: {
                        return Math.abs(b.contribution ?? 0) - Math.abs(a.contribution ?? 0)
                    }
                }
            }
        }
        return criteria ? [...indexComponents].sort(sortByProperty(criteria)) : indexComponents
    }, [])

    useEffect(() => {
        websocketActions.setWebsocketSubscribeList(dispatch, {
            orderBookSubscriptions: [
                {
                    tag: 'indexComponentOrderBook',
                    securities: securitiesToSubscribe.map((sec) => ({
                        exchange: sec.exchange,
                        code: sec.code
                    })),
                    topBidAskOnly: false
                }
            ]
        })
    }, [securitiesToSubscribe])

    const subscribeOrderBook = useCallback(
        (security: Security, subscribe: boolean) => {
            if (subscribe) {
                setSecuritiesToSubscribe([...securitiesToSubscribe, security])
            } else {
                setSecuritiesToSubscribe(securitiesToSubscribe.filter((listSecurity) => listSecurity.code !== security.code))
            }
        },
        [securitiesToSubscribe]
    )

    const onIndexSortMenuClick = useCallback(
        (value: string) => {
            commonActions.setOverviewIndexComponentsSortedBy(dispatch, value)
        },
        [dispatch]
    )

    const indexComponentData = useMemo(() => {
        const rows: IndexComponentData[] = []
        indexComponents.forEach((component: OverviewWidgetResponse_IndexComponent) => {
            const prevClose = component.previousClose
            const change = prevClose !== 0 ? component.ltp - prevClose : null
            const changePercentage = prevClose !== 0 ? (change! / prevClose) * 100 : null
            const contribution = change ? component.spi * change : null
            rows.push({
                id: component.security!.code,
                security: component.security!,
                ltp: component.ltp / (component.security!.priceMultiplier ?? 100),
                changePercentage: changePercentage,
                contribution: contribution ? contribution / (component.security!.priceMultiplier ?? 100) : null,
                weightage: component.weightage,
                previousClose: component.previousClose,
                spi: component.spi,
            })
        })
        return sortIndexComponents(rows, resolvedSortCriteria)
    }, [indexComponents, resolvedSortCriteria, sortIndexComponents])

    const isIndexComponentsVisible = indexComponentData.length > 0

    useEffect(() => {
        const indexSecurities: Security[] = []
        indexComponents.forEach((component: OverviewWidgetResponse_IndexComponent) => {
            if (component.security)
                indexSecurities.push({
                    exchange: component.security.exchange,
                    code: component.security.code,
                    companyCode: component.security.companyCode
                })
        })
        if (isExpanded) {
            setLimit(indexComponents.length)
        } else {
            setLimit(initialVisibleLimit)
        }
        websocketActions.setWebsocketSubscribeList(dispatch, {
            securitySnapshotSubscriptions: [
                {
                    tag: subscriptionTags.INDEX_COMPONENT,
                    securities: indexSecurities
                }
            ]
        })
    }, [dispatch, indexComponents, isExpanded, initialVisibleLimit])

    const onChipClick = useCallback(
        (limit: any) => {
            const shouldExpand = limit !== indexComponentData?.length
            setIsExpanded(shouldExpand)
            setLimit(shouldExpand ? indexComponentData?.length : initialVisibleLimit)
        },
        [indexComponentData, initialVisibleLimit]
    )

    return isIndexComponentsVisible ? (
        <Box sx={{ width: '100%' }}>
            <TableContainer style={{ overflowX: 'hidden', width: '100%', overflow: 'auto' }}>
                <Table
                    stickyHeader
                    style={{ borderCollapse: 'separate', fontSize: 13 }}
                    sx={{
                        tableLayout: 'fixed',
                        '& .MuiTableCell-head': { fontWeight: 400 },
                        '& .MuiTableSortLabel-root': { fontWeight: 400 },
                        '& .MuiTableSortLabel-root.Mui-active': { fontWeight: 400 },
                    }}
                    className={'font-feature-num'}
                >
                    <TableHead>
                        <TableRow sx={{ height: 32, borderBottomColor: (theme) => theme.palette.divider }}>
                            <TableCell
                                width="36%"
                                sx={{ py: 0.5, px: 1 }}
                                sortDirection={resolvedSortCriteria === 'ascending_symbol' ? 'asc' : resolvedSortCriteria === 'descending_symbol' ? 'desc' : false}
                            >
                                <TableSortLabel
                                    sx={{ fontWeight: 400 }}
                                    active={indexComponentsSortedBy === 'ascending_symbol' || indexComponentsSortedBy === 'descending_symbol'}
                                    direction={indexComponentsSortedBy === 'ascending_symbol' ? 'asc' : indexComponentsSortedBy === 'descending_symbol' ? 'desc' : 'asc'}
                                    onClick={() => {
                                        let next: string
                                        if (indexComponentsSortedBy === 'ascending_symbol') next = 'descending_symbol'
                                        else if (indexComponentsSortedBy === 'descending_symbol') next = ''
                                        else next = 'ascending_symbol'
                                        onIndexSortMenuClick(next)
                                    }}
                                >
                                    Components
                                </TableSortLabel>
                            </TableCell>
                            {showContribution && (
                                <TableCell
                                    width="22%"
                                    align="right"
                                    sx={{ py: 0.5, px: 1, position: 'relative' }}
                                    sortDirection={indexComponentsSortedBy === 'ascending_contribution' ? 'asc' : indexComponentsSortedBy === 'descending_contribution' ? 'desc' : false}
                                >
                                    <TableSortLabel
                                        sx={{ width: '100%', fontWeight: 400 }}
                                        active={indexComponentsSortedBy === 'ascending_contribution' || indexComponentsSortedBy === 'descending_contribution'}
                                        direction={indexComponentsSortedBy === 'ascending_contribution' ? 'asc' : indexComponentsSortedBy === 'descending_contribution' ? 'desc' : 'asc'}
                                        onClick={() => {
                                            let next: string
                                            if (indexComponentsSortedBy === 'ascending_contribution') next = 'descending_contribution'
                                            else if (indexComponentsSortedBy === 'descending_contribution') next = ''
                                            else next = 'ascending_contribution'
                                            onIndexSortMenuClick(next)
                                        }}
                                    >
                                        Contribution
                                    </TableSortLabel>
                                </TableCell>
                            )}
                            <TableCell
                                width="20%"
                                align="right"
                                sx={{ py: 0.5, px: 1 }}
                                sortDirection={indexComponentsSortedBy === 'ascending_change' ? 'asc' : indexComponentsSortedBy === 'descending_change' ? 'desc' : false}
                            >
                                <TableSortLabel
                                    sx={{ fontWeight: 400 }}
                                    active={indexComponentsSortedBy === 'ascending_change' || indexComponentsSortedBy === 'descending_change'}
                                    direction={indexComponentsSortedBy === 'ascending_change' ? 'asc' : indexComponentsSortedBy === 'descending_change' ? 'desc' : 'asc'}
                                    onClick={() => {
                                        let next: string
                                        if (indexComponentsSortedBy === 'ascending_change') next = 'descending_change'
                                        else if (indexComponentsSortedBy === 'descending_change') next = ''
                                        else next = 'ascending_change'
                                        onIndexSortMenuClick(next)
                                    }}
                                >
                                    Chg%
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                width="22%"
                                align="right"
                                sx={{ py: 0.5, px: 1 }}
                                sortDirection={indexComponentsSortedBy === 'ascending_ltp' ? 'asc' : indexComponentsSortedBy === 'descending_ltp' ? 'desc' : false}
                            >
                                <TableSortLabel
                                    sx={{ fontWeight: 400 }}
                                    active={indexComponentsSortedBy === 'ascending_ltp' || indexComponentsSortedBy === 'descending_ltp'}
                                    direction={indexComponentsSortedBy === 'ascending_ltp' ? 'asc' : indexComponentsSortedBy === 'descending_ltp' ? 'desc' : 'asc'}
                                    onClick={() => {
                                        let next: string
                                        if (indexComponentsSortedBy === 'ascending_ltp') next = 'descending_ltp'
                                        else if (indexComponentsSortedBy === 'descending_ltp') next = ''
                                        else next = 'ascending_ltp'
                                        onIndexSortMenuClick(next)
                                    }}
                                >
                                    LTP
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(showContribution ? indexComponentData.slice(0, limit) : indexComponentData).map((indexComponent) => (
                            <ComponentView
                                key={indexComponent.security?.code}
                                component={indexComponent}
                                subscribeSecurity={subscribeOrderBook}
                                showContribution={showContribution}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {showContribution && (
                <Stack direction="row-reverse" spacing={1} sx={{ m: 0.5 }}>
                    <Link color={"primary"} underline="hover" sx={{ fontWeight: 400 }} data-event={limit === indexComponentData.length ? 'View Less Clicked' : 'View More Clicked'} onClick={() => onChipClick(limit)} variant={'caption'}>
                        {limit === indexComponentData.length ? `show only top ${initialVisibleLimit} components` : `show all ${indexComponentData.length} components`}
                    </Link>
                </Stack>
            )}
        </Box>
    ) : null
}

export default memo(IndexComponents)
